import {useCallback, useEffect, useMemo} from 'react'

import {useSyncedBooleanQueryParam} from '../../../../../../../hooks/routes'
import {getSingleBuildArg} from '../../../../../../../rest/builds'
import {getBuildLocator} from '../../../../../../../rest/locators'
import {restApi} from '../../../../../../../services/rest'
import {subscribeOnBuildTypeEvents} from '../../../../../../../utils/subscriber'
import {BUILD_CHANGES_LOADED, BUILD_FINISHED} from '../../../../../../../utils/subscriptionEvents'
import {UserProperties} from '../../../../../../../utils/userProperties'
import {DEFAULT_CHANGES_LIST_CONTEXT} from '../../../../../../packages/Changes/Change/Change.context'
import {useChangeLogLocator} from '../../../../../../packages/Changes/ChangesLog/ChangesLog.hooks'
import {getChangeLogArg} from '../../../../../../packages/Changes/ChangesLog/ChangesLog.utils'

function useChangesTab(jobId: number) {
  const [showFiles] = useSyncedBooleanQueryParam({
    param: 'showFiles',
    userProperty: UserProperties.SHOW_ALL_CHANGES_FILES,
  })

  const changesListContextValue = useMemo(
    () => ({...DEFAULT_CHANGES_LIST_CONTEXT, showFiles, withRevision: false}),
    [showFiles],
  )
  const {withGraph} = changesListContextValue

  const {headId, headInternalId, queued, running, refetch} =
    restApi.endpoints.getBuildNormalized.useQuery(
      getSingleBuildArg(getBuildLocator(jobId), {
        withBuildTypeDetails: true,
      }),
      {
        selectFromResult: ({data}) => {
          const run = data?.entities?.builds?.[data?.result!]
          const headId = run?.buildType
          const job = data?.entities?.builds?.[jobId!]
          return {
            headId,
            headInternalId: data?.entities?.buildTypes?.[headId!]?.internalId,
            queued: job?.state === 'queued',
            running: job?.state === 'running',
          }
        },
      },
    )

  const [fetchChangeLog, {hasChangeLog, isChangeLogReady}] =
    restApi.endpoints.getChangeLog.useLazyQuery({
      selectFromResult: ({data, currentData}) => ({
        hasChangeLog: data?.rows != null && data.rows.length > 0,
        isChangeLogLoading: data !== currentData,
        isChangeLogReady: data != null,
      }),
    })

  const locator = useChangeLogLocator({buildId: jobId, buildTypeId: headId})

  const changeLogArg = useMemo(
    () =>
      locator != null
        ? getChangeLogArg({
            locator,
            changesOptions: {withFiles: showFiles, withUpdatePager: true},
            withGraph,
          })
        : null,
    [locator, showFiles, withGraph],
  )

  const fetch = useCallback(() => {
    if (changeLogArg != null) {
      fetchChangeLog(changeLogArg)
    }
  }, [changeLogArg, fetchChangeLog])

  useEffect(
    () =>
      headInternalId
        ? subscribeOnBuildTypeEvents(headInternalId, [BUILD_FINISHED], () => {
            refetch()
          })
        : undefined,
    [headInternalId, refetch],
  )

  useEffect(() => {
    if (headInternalId != null && (queued || running)) {
      return subscribeOnBuildTypeEvents(headInternalId, [BUILD_CHANGES_LOADED], fetch)
    } else {
      fetch()
      return undefined
    }
  }, [headInternalId, fetch, queued, running])

  return {
    headId,
    headInternalId,
    locator,
    queued,
    changesListContextValue,
    hasChangeLog,
    isChangeLogReady,
  }
}

export default useChangesTab
